import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useSupportOptional } from '@/lib/support-context';
import { BRAND } from '@maintenance-tracker/shared';

const LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/contact', label: 'Contact' },
  { to: '/remove-report', label: 'Remove my report' },
] as const;

/** Contact opens the Help widget on public pages (falls back to /contact when unavailable). */
function FooterLink({ to, label, className }: { to: string; label: ReactNode; className?: string }) {
  const support = useSupportOptional();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (to === '/contact' && support && pathname !== '/contact') {
    return (
      <button type="button" className={className} onClick={() => support.openSupport('footer_contact')}>
        {label}
      </button>
    );
  }
  return (
    <Link to={to} className={className}>
      {label}
    </Link>
  );
}

type LegalFooterProps = {
  variant?: 'landing' | 'compact';
};

export function LegalFooter({ variant = 'landing' }: LegalFooterProps) {
  if (variant === 'compact') {
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-[13px] text-text-secondary">
        {LEGAL_LINKS.map((l) => (
          <FooterLink key={l.to} to={l.to} label={l.label} className="hover:text-brand" />
        ))}
      </div>
    );
  }

  return (
    <footer className="border-t border-border px-4 py-10">
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-text-secondary">
          {LEGAL_LINKS.map((l) => (
            <FooterLink key={l.to} to={l.to} label={l.label} className="hover:text-text-primary" />
          ))}
        </div>
        <div className="flex flex-col items-center justify-between gap-2 text-center text-sm text-text-secondary md:flex-row md:text-left">
          <p>{BRAND.copyright}</p>
          <a href={`mailto:${BRAND.helloEmail}`} className="hover:text-text-primary">
            {BRAND.helloEmail}
          </a>
        </div>
      </div>
    </footer>
  );
}
